import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import FlexBox from '../FlexBox';
import Icon from '../Icon';
import { getLabel } from '../../utils/i18n';
import { decimalPlaces, floor, formatNumber } from '../../utils/number';
import { Link } from '../../routes';
import { MARGIN_ORDER_ENUM } from '../../actions/actionsSet/order';

export const getOrderTypeName = (i18n) => ({
  'limit': getLabel('exchange-trade-LimitOrder', i18n),
  'market': getLabel('exchange-trade-MarketOrder', i18n),
  'stop-limit': getLabel('exchange-trade-StopLimit', i18n),
  'oco': getLabel('exchange-trade-OCO', i18n),
});

const Wrap = styled.div`
  font-family: DINPro, IBMPlexSans-Light, "Microsoft YaHei", PingFangSC-Regular, Arial, sans-serif;
  font-style: normal;
  font-weight: normal;
  position: relative;
  > a {
    cursor: pointer;
    position: absolute;
    right: -26px;
    top: -30px;
    :hover .iconfont {
      color: rgb(240, 185, 11);
    }
  }
`;


const SideBox = styled.span`
  color: ${({ side }) => side === 'BUY' ? '#70a800' : '#ea0070'};
`;

const Title = styled.div`
  padding-bottom: 15px;
  margin-bottom: 15px;
  text-align: center;
  border-bottom: 1px solid #EAECEF;
  font-size: 16px;
  color: #212833;
  line-height: 20px;
  font-weight: bold;
  font-family: DINPro-Medium, IBMPlexSans-Medium, "Microsoft YaHei", PingFangSC-Regular, Arial, sans-serif;
`;

const OrderDetails = styled.div`
  widlth: 100%;
`;

const OrderItem = styled(FlexBox)`
  width: 100%;
  font-size: 14px;
  color: #48515D;
  line-height: 20px;
  > strong {
    font-family: DINPro-Medium, IBMPlexSans-Medium, "Microsoft YaHei", PingFangSC-Regular, Arial, sans-serif;
    font-weight: bold;
    color: #212833;
  }
`;

const StopLimitOrderTip = styled.div`
  margin-top:16px;
  color: #48515D;
  line-height: 16px;
  > strong {
    font-weight: bold;
  }
`;

const MarginTip = styled(FlexBox)`
 color: #212833;
 line-height: 17px;
 padding: 16px 0;
 margin-top: 16px;
 border-top: 1px solid #EAECEF;
 :first-of-type {
   border-top: 1px solid #EAECEF;
 }
 .iconfont {
   margin-right: 8px;
 }
 strong {
  font-family: DINPro-Medium, IBMPlexSans-Medium, "Microsoft YaHei", PingFangSC-Regular, Arial, sans-serif;
  color: rgb(240, 185, 11);
 }
`;

const getStopLimitTip = (payload, props) => {
  const { price, stopPrice, quantity } = payload;
  const {
    pair: [base, quote],
    locale,
    side,
    ticker,
    orderType,
  } = props;
  const latestPrice = +ticker.c;
  if (orderType !== 'stop-limit') return null;
  if (locale === 'cn') {
    return (
      <StopLimitOrderTip>
        当价格{stopPrice >= latestPrice ? '上涨' : '下跌'}至或{stopPrice >= latestPrice ? '高于' : '低于'}
        <strong>{stopPrice} {quote}</strong>时，则触发以<strong>{price} {quote}</strong>的价格
        {side === 'BUY' ? '买入' : '卖出'}<strong>{quantity} {base}</strong>的订单。
      </StopLimitOrderTip>
    );
  }
  return (
    <StopLimitOrderTip>
      If the last price{stopPrice >= latestPrice ? ' rises to or above ' : ' drops to or below '}
      <strong>{stopPrice} {quote}</strong>, an order to {side === 'BUY' ? 'buy' : 'sell'} <strong>{quantity} {base}</strong>
          &nbsp;at a price of <strong>{price} {quote}</strong> will be placed.
    </StopLimitOrderTip>
  );
};

const getMarginBorrowTip = (payload, borrowAmount, props) => {
  const { quantity } = payload;
  const {
    pair: [base, quote],
    marginFreeAssets,
    tickSize,
    locale,
    side,
  } = props;
  if (!borrowAmount) return null;
  const ts = decimalPlaces(tickSize);
  const assetName = side === 'BUY' ? quote : base;
  const free = floor(marginFreeAssets[assetName], ts);
  if (locale === 'cn') {
    return (
      <MarginTip wrap="nowrap">
        <Icon type="prompt" size={14} color="#848E9C" />
        <div>
         使用您账户剩余的<strong>{free} {assetName}</strong>资产，同时借入<strong>{borrowAmount} {assetName}</strong>,
         用于{side === 'BUY' ? '购买' : '卖出'}<strong>{quantity} {base}</strong>。借款将承担利息。
        </div>
      </MarginTip>
    );
  }
  return (
    <MarginTip wrap="nowrap">
      <Icon type="prompt" size={14} color="#848E9C" />
      <div>
      The order will use <strong>{free} {assetName}</strong> in your margin account,
      and will also borrow <strong>{borrowAmount} {assetName}</strong> on your behalf, to {side === 'BUY' ? 'buy' : 'sell'} <strong>{quantity} {base}</strong> .
      The loan will have interests.
      </div>
    </MarginTip>
  );
};

/* eslint-disable react/prop-types */
const getAutoRepayTip = (payload, sideEffectType, props) => {
  const {
    pair: [base, quote],
    locale,
    marginUserAssets,
    side,
  } = props;
  const debtAssetName = side === 'BUY' ? base : quote;
  const debt = marginUserAssets.filter(m => m.asset === debtAssetName)[0]?.totalDebt;
  if (!Number(debt) || sideEffectType !== MARGIN_ORDER_ENUM.AUTO_REPAY) return null;
  if (locale === 'cn') {
    return (
      <MarginTip wrap="nowrap">
        <Icon type="prompt" size={14} color="#848E9C" />
        <div>
          交易获得的 <strong>{debtAssetName}</strong> 将用于偿还您的 <strong> {debtAssetName}</strong> 欠款，优先偿还利息。
        </div>
      </MarginTip>
    );
  }
  return (
    <MarginTip wrap="nowrap">
      <Icon type="prompt" size={14} color="#848E9C" />
      <div>
        The <strong>{debtAssetName}</strong> you get will be used to repay the <strong> {debtAssetName}</strong>
        debt in your margin account. Interests will be repaid first.
      </div>
    </MarginTip>
  );
};


const OrderConfirmation = ({ payload, borrowAmount, props }) => {
  const {
    price, stopPrice, quantity, sideEffectType,
  } = payload;
  const {
    pair: [base, quote],
    tickSize,
    minTrade,
    i18n,
    orderType,
    side,
    ticker,
    locale,
  } = props;
  if (!ticker.c) return '';
  const ts = decimalPlaces(tickSize);
  const mt = decimalPlaces(minTrade);
  const tdp = (ts + mt) < 8 ? (ts + mt) : 8;
  return (
    <Wrap>
      <Link route={`/${locale}/usercenter/settings/preference`}>
        <a target="_blank">
          <Icon type="seeting" size={20} color="#848E9C" />
        </a>
      </Link>
      <Title>{getLabel('exchange-preference-orderConfirmation', i18n)}</Title>
      <OrderDetails>
        <OrderItem justify="space-between" margin="0 0 8px">
          <strong>{`${base}/${quote}`}</strong>
          <strong>{getOrderTypeName(i18n)[orderType]} - <SideBox side={side}>{getLabel(`exchange-trade-${side}`, i18n)}</SideBox></strong>
        </OrderItem>
        {orderType === 'stop-limit' && (
          <Fragment>
            <OrderItem justify="space-between">
              <span>{getLabel('exchange-trade-StopPrice', i18n)}: </span>
              <span>{formatNumber(stopPrice, ts)}</span>
            </OrderItem>
            <OrderItem justify="space-between">
              <span>{getLabel('exchange-trade-LimitPrice', i18n)}: </span>
              <span>{formatNumber(price, ts)}</span>
            </OrderItem>
          </Fragment>
        )}
        {orderType === 'limit' && (
          <OrderItem justify="space-between">
            <span>{getLabel('exchange-trade-Price', i18n)}: </span>
            <span>{formatNumber(price, ts)}</span>
          </OrderItem>
        )}
        {orderType === 'market' && (
          <OrderItem justify="space-between">
            <span>{getLabel('exchange-trade-Price', i18n)}: </span>
            <span>{getLabel('exchange-trade-MarketPrice', i18n)}</span>
          </OrderItem>
        )}
        <OrderItem justify="space-between">
          <span>{getLabel('exchange-common-amount', i18n)}: </span>
          <span>{formatNumber(quantity, mt)}</span>
        </OrderItem>
        {orderType !== 'market' && (
          <OrderItem justify="space-between">
            <span>{getLabel('exchange-trade-Total', i18n)}: </span>
            <span>{formatNumber(price * quantity, tdp)}</span>
          </OrderItem>
        )}
      </OrderDetails>
      {getStopLimitTip(payload, props)}
      {getMarginBorrowTip(payload, borrowAmount, props)}
      {getAutoRepayTip(payload, sideEffectType, props)}
    </Wrap>
  );
};

const propTypes = {
  payload: PropTypes.object.isRequired,
  props: PropTypes.object.isRequired,
  i18n: PropTypes.object.isRequired,
  pair: PropTypes.array.isRequired,
  marginFreeAssets: PropTypes.object.isRequired,
  tickSize: PropTypes.number.isRequired,
  minTrade: PropTypes.number.isRequired,
  locale: PropTypes.string.isRequired,
  orderType: PropTypes.string.isRequired,
  side: PropTypes.string.isRequired,
  ticker: PropTypes.object.isRequired,
  borrowAmount: PropTypes.bool.isRequired,
  userInfo: PropTypes.object.isRequired,
  marginUserAssets: PropTypes.object.isRequired,
};

OrderConfirmation.propTypes = propTypes;

getStopLimitTip.propTypes = propTypes;

getMarginBorrowTip.propTypes = propTypes;

export default OrderConfirmation;
